import { toast } from 'sonner';
import { create } from 'zustand';
import { HttpRequestAIActionFromTextMode } from '../api/Action/dto/Action.dto';
import i18n from '@/Config/app/i18n';

type Params = Record<string, any> | null;

export const MODAL_NAMES = {
  // Custom action modals
  CAMPAIGN_SHEET: 'CAMPAIGN_SHEET',
  HANDOFF_ACTION_SHEET: 'HANDOFF_ACTION_SHEET',
  // HttpRequestAIAction modals
  HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL: 'HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL',
  CREATE_HTTP_REQUEST_ACTION_DIALOG: 'CREATE_HTTP_REQUEST_ACTION_DIALOG',
  HTTP_REQUEST_ACTION_SHEET: 'HTTP_REQUEST_ACTION_SHEET',
  HUBSPOT_TEMPLATE_GALLERY_DIALOG: 'HUBSPOT_TEMPLATE_GALLERY_DIALOG',
  // Remaining action modals
  REMOVE_ACTION_MODAL_NAME: 'REMOVE_ACTION_MODAL_NAME',
  KNOWLEDGE_BASE_ACTION_SHEET: 'KNOWLEDGE_BASE_ACTION_SHEET',
  REMOTE_MCP_DIALOG: 'REMOTE_MCP_DIALOG',
  SHOPIFY_MCP_DIALOG: 'SHOPIFY_MCP_DIALOG',
  // Add article modals
  ADD_KNOWLEDGE_MODAL_ID: 'add-knowledge-modal',
  UPLOAD_MODAL_ID: 'upload-files-modal',
  UPLOAD_URL_MODAL_ID: 'upload-url-modal',
  UPLOAD_SITEMAP_MODAL_ID: 'upload-sitemap-modal',
  UPLOAD_MULTIPLE_URLS_MODAL_ID: 'upload-multiple-urls-modal',
  UPLOAD_ALL_PAGES_MODAL_ID: 'upload-all-pages-modal',
  SITE_SCRAPE_REVIEW_MODAL_ID: 'site-scrape-review-modal',
  UPLOAD_YOUTUBE_MODAL_ID: 'upload-youtube-modal',
  // Remaining KnowledgeBase modals
  ARTICLE_LIMIT_MODAL: 'ARTICLE_LIMIT_MODAL',
  DELETE_ARTICLES_DIALOG: 'DELETE_ARTICLES_DIALOG',
  REFRESH_ARTICLES_DIALOG: 'REFRESH_ARTICLES_DIALOG',
  REFRESH_ARTICLES_LIMIT_DIALOG: 'REFRESH_ARTICLES_LIMIT_DIALOG',
  AUTO_TAG_ARTICLES_DIALOG: 'AUTO_TAG_ARTICLES_DIALOG',
  ARTICLE_SHEET: 'ARTICLE_SHEET',
  PARAGRAPH_SHEET: 'PARAGRAPH_SHEET',
  // Integration modals
  MCP_INTEGRATION_SHEET: 'MCP_INTEGRATION_SHEET',
  WHATSAPP_PHONE_NUMBERS_SHEET: 'WHATSAPP_PHONE_NUMBERS_SHEET',
  CREATE_TOKEN_DIALOG: 'CREATE_TOKEN_DIALOG',
  SHOPIFY_CONNECT_MODAL_ID: 'SHOPIFY_CONNECT_MODAL_ID',
  INTERCOM_ARTICLES_MODAL_ID: 'INTERCOM_ARTICLES_MODAL_ID',
  // Remaining modals
  EXPERT_CALL_MODAL_ID: 'EXPERT_CALL_MODAL_ID',
} as const;

function toastOnToggleModalFail(modalName: string, params?: Record<string, any>): boolean {
  switch (modalName) {
    case MODAL_NAMES.HTTP_REQUEST_AI_ACTION_FROM_TEXT_MODAL: {
      if (params?.mode == HttpRequestAIActionFromTextMode.CREATED) {
        toast.success(
          i18n.t('common:toast.toggle_modal_fail.http_request_ai_action_from_text_created'),
        );
      } else {
        toast.error(
          i18n.t('common:toast.toggle_modal_fail.http_request_ai_action_from_text_failed'),
        );
      }

      return true;
    }
  }
  return false;
}

const PROTECTED_MODALS_NAMES = [
  MODAL_NAMES.CAMPAIGN_SHEET,
  MODAL_NAMES.HANDOFF_ACTION_SHEET,
  MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET,
  MODAL_NAMES.KNOWLEDGE_BASE_ACTION_SHEET,
  MODAL_NAMES.ARTICLE_SHEET,
] as const;

type ProtectedModalName = (typeof PROTECTED_MODALS_NAMES)[number];

type ModalStore = {
  name: string | null;
  open: boolean;
  params: Record<string, any> | null;
  isOpen: (name: string) => boolean;
  isProtectedModalOpen: () => boolean;
  toggleModal: (name: string, params?: Record<string, any>) => void;
  closeModal: () => void;
  getParams: () => Params;
  setParams: (patch: Params) => void;
};

export const useModalStore = create<ModalStore>((set, get) => ({
  name: null,
  open: false,
  params: null,
  isProtectedModalOpen: () => {
    const { name, open } = get();
    if (!open || !name) return false;
    return PROTECTED_MODALS_NAMES.includes(name as ProtectedModalName);
  },
  toggleModal: (name: string, params?: Record<string, any>) => {
    const { name: currentName, open, isProtectedModalOpen } = get();
    if (currentName === name) {
      set({ open: !open, params: params ?? null });
    } else {
      // do not override modal in case of ProtecteModalOpen and fallbackToast existing
      if (isProtectedModalOpen()) {
        const fallbackToastEmitted = toastOnToggleModalFail(name, params);
        if (fallbackToastEmitted) return;
      }
      if (typeof window.closeHtmlModal === 'function') {
        window.closeHtmlModal();
      }
      set({ name, open: true, params: params ?? null });
    }
  },
  isOpen: (name: string) => get().name === name && get().open,
  closeModal: () => set({ open: false, name: null, params: null }),
  getParams: () => get().params,
  setParams: (patch) => set((state) => ({ params: { ...(state.params ?? {}), ...patch } })),
}));
